import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { SideListModel } from '@fi-app/utilities/side-list/side-list.model';
import { FiSideListType } from '@fi-app/utilities/side-list/side-list-type';
import { comparator } from '@fi-shared/utils/array-sort-comparators';
import {
    IContentLanguage,
    IContentLanguageLabelModel,
} from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { isDefined } from '@fi-shared/utils';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { IEditObjectDialogData, IEditObjectDialogDataItem } from '@fi-shared/fi-edit-object-dialog/fi-edit-object';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { ContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/content-language-label.model';

@Component({
    selector: 'fi-object-edit-names',
    templateUrl: './fi-object-edit-names.component.html',
    styleUrls: ['./fi-object-edit-names.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-object-edit-names' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiObjectEditNamesComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiObjectEditNamesComponent,
        },
    ],
    standalone: false,
})
export class FiObjectEditNamesComponent
    extends ControlValueAccessorValidatorBase<IEditObjectDialogDataItem>
    implements OnInit
{
    get changedItem(): IEditObjectDialogDataItem {
        return this.ngModel;
    }
    @Input() dialogData: IEditObjectDialogData;

    selectedTabIndex: number;
    selectedContentLanguageId: number;
    ccmSideListModel: SideListModel;
    nameInputPlaceholder: string;

    constructor(
        private injector: Injector,
        private contentLanguagesService: ContentLanguagesService,
        private userDataService: FiUserDataService,
        private fiContentLanguageSharedService: FiContentLanguageSharedService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.setLanguagesTabs();
        this.addMissedLanguageLabels();
        this.updatePlaceholder();
    }

    private addMissedLanguageLabels() {
        this.fiContentLanguageSharedService.initializeContentLanguagesArray(
            this.changedItem.Names,
            this.changedItem.Names,
            this.dialogData.useApplicationLanguages,
        );
        if (!this.dialogData.isDescriptionsHidden) {
            this.fiContentLanguageSharedService.initializeContentLanguagesArray(
                this.changedItem.Descriptions,
                this.changedItem.Descriptions,
                this.dialogData.useApplicationLanguages,
            );
        }
    }

    private updatePlaceholder() {
        if (this.dialogData.isPrioritizedContentLanguagePlaceholderShown) {
            this.nameInputPlaceholder = this.getPrioritizedContentLanguageLabelValue(this.changedItem.Names);
        }
    }

    onSelectedIndexChange($event: number) {
        this.selectedContentLanguageId = this.ccmSideListModel.Data[$event].Id;
        this.ccmSideListModel.ActiveItem = this.selectedContentLanguageId;
        this.updatePlaceholder();
    }

    private setLanguagesTabs() {
        this.selectedContentLanguageId = this.getInitialSelectedContentLanguageId();

        this.ccmSideListModel = new SideListModel(
            FiSideListType.Translations,
            this.userDataService.applicationLanguage,
            this.userDataService.userData.PreferredContentLanguage,
        );
        this.ccmSideListModel.Data = this.contentLanguagesService
            .GetApplicationLanguages()
            .sort(comparator((item) => item.SortIndex));
        this.ccmSideListModel.DefaultItem = this.selectedContentLanguageId;

        this.selectedTabIndex = this.ccmSideListModel.Data.findIndex((language: IContentLanguage) => {
            return this.isTabActive(language);
        });
    }

    private getInitialSelectedContentLanguageId(): number {
        return isDefined(this.dialogData.selectedLanguageId)
            ? this.dialogData.selectedLanguageId
            : this.contentLanguagesService.getSelectedLanguageIdByApplicationLang(this.changedItem.Names);
    }

    isTabActive(tab: IContentLanguage): boolean {
        return tab.Id === this.selectedContentLanguageId;
    }

    private getPrioritizedContentLanguageLabelValue(contentLanguageLabelModels: IContentLanguageLabelModel[]): string {
        return this.fiContentLanguageSharedService.getPrioritizedContentLanguageLabelValue({
            contentLanguageLabelModels: contentLanguageLabelModels,
            currentSelectedLanguageId: this.selectedContentLanguageId,
            isLanguageNameShown: true,
        });
    }

    getCurrentNameModel(): IContentLanguageLabelModel {
        return this.getCurrentContentLanguageLabelModel(this.changedItem.Names);
    }

    getCurrentDescriptionModel(): IContentLanguageLabelModel {
        return this.getCurrentContentLanguageLabelModel(this.changedItem.Descriptions);
    }

    private getCurrentContentLanguageLabelModel(contentLanguageLabelModels): IContentLanguageLabelModel {
        return contentLanguageLabelModels.find((data: IContentLanguageLabelModel) => {
            return data.ContentLanguageId === this.selectedContentLanguageId;
        });
    }
}
